import React, { useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid,
  Chip,
  Divider
} from '@mui/material';
import {
  filterTelemetryByLap,
  getTelemetryAtTime,
  convertSpeed,
  calculateGForces,
  getTelemetryStats
} from './utils/telemetryProcessor';

/**
 * Telemetry display panel component
 * Shows real-time telemetry data and basic charts
 */
function TelemetryPanel({
  telemetryData,
  currentTime = 0,
  currentLap = 0,
  lapData = [],
  showCharts = true,
  showRealTimeData = true,
  showLapStats = true,
  className = ''
}) {

  // Get current telemetry data point
  const currentTelemetry = useMemo(() => {
    if (!telemetryData?.locations || !telemetryData.session?.startDateTime) {
      return null;
    }
    
    return getTelemetryAtTime(
      telemetryData.locations,
      currentTime,
      telemetryData.session.startDateTime
    );
  }, [telemetryData, currentTime]);

  // Get current lap telemetry data
  const currentLapTelemetry = useMemo(() => {
    if (!telemetryData?.locations) return [];
    
    return filterTelemetryByLap(
      telemetryData.locations, 
      currentLap, 
      lapData, 
      telemetryData.session?.startDateTime
    );
  }, [telemetryData, currentLap, lapData]);

  // Calculate current G-forces
  const currentGForces = useMemo(() => {
    if (!currentTelemetry) return { lateral: 0, longitudinal: 0, vertical: 0 };
    
    return calculateGForces(currentTelemetry);
  }, [currentTelemetry]);

  // Calculate lap statistics
  const lapStats = useMemo(() => {
    if (currentLapTelemetry.length === 0) return null;
    
    return {
      speed: getTelemetryStats(currentLapTelemetry, 'speed'),
      maxG: Math.max(
        ...currentLapTelemetry.map(point => {
          const gForces = calculateGForces(point);
          return Math.max(Math.abs(gForces.lateral), Math.abs(gForces.longitudinal));
        })
      )
    };
  }, [currentLapTelemetry]);

  if (!telemetryData) {
    return (
      <Box className={className} sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          No Telemetry Data
        </Typography>
        <Typography variant="body2" color="text.secondary">
          This session doesn't have telemetry data available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box 
      className={className} 
      sx={{ 
        p: 1, 
        height: '100%', 
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0
      }}
    >
      <Typography variant="h6" gutterBottom sx={{ flexShrink: 0 }}>
        Telemetry Data
      </Typography>
      
      {/* Scrollable content area */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', minHeight: 0 }}>

      {/* Real-time Data */}
      {showRealTimeData && currentTelemetry && (
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ pb: '16px !important' }}>
            <Typography variant="subtitle2" gutterBottom>
              Current Values
            </Typography>
            
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="primary">
                    {Math.round(convertSpeed(currentTelemetry.speed || 0, 'mph'))}
                  </Typography>
                  <Typography variant="caption">MPH</Typography>
                </Box>
              </Grid>
              
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="secondary">
                    {currentGForces.lateral.toFixed(1)}
                  </Typography>
                  <Typography variant="caption">Lateral G</Typography>
                </Box>
              </Grid>
              
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body1">
                    {currentGForces.longitudinal.toFixed(1)}
                  </Typography>
                  <Typography variant="caption">Long G</Typography>
                </Box>
              </Grid>
              
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body1">
                    {Math.round(currentTelemetry.bearing || 0)}°
                  </Typography>
                  <Typography variant="caption">Bearing</Typography>
                </Box>
              </Grid>
              
              {/* Distance information if available */}
              {(currentTelemetry.distance || currentTelemetry.cumulativeDistance) && (
                <>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body1">
                        {currentTelemetry.cumulativeDistance ? 
                          Math.round(currentTelemetry.cumulativeDistance) : 
                          Math.round(currentTelemetry.distance || 0)}m
                      </Typography>
                      <Typography variant="caption">
                        {currentTelemetry.cumulativeDistance ? 'Total Dist' : 'Distance'}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  {currentTelemetry.segmentNumber && (
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body1">
                          Seg {currentTelemetry.segmentNumber}
                        </Typography>
                        <Typography variant="caption">Segment</Typography>
                      </Box>
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Lap Statistics */}
      {showLapStats && lapStats && (
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ pb: '16px !important' }}>
            <Typography variant="subtitle2" gutterBottom>
              Lap {currentLap} Stats
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Max Speed:</Typography>
                <Chip 
                  label={`${Math.round(convertSpeed(lapStats.speed.max, 'mph'))} mph`}
                  size="small" 
                  color="primary"
                />
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Avg Speed:</Typography>
                <Chip 
                  label={`${Math.round(convertSpeed(lapStats.speed.avg, 'mph'))} mph`}
                  size="small"
                />
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Max G-Force:</Typography>
                <Chip 
                  label={`${lapStats.maxG.toFixed(1)}G`}
                  size="small" 
                  color="secondary"
                />
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Data Points:</Typography>
                <Chip 
                  label={currentLapTelemetry.length}
                  size="small"
                />
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Session Info */}
      <Card>
        <CardContent sx={{ pb: '16px !important' }}>
          <Typography variant="subtitle2" gutterBottom>
            Session Info
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="body2">
              <strong>Track:</strong> {telemetryData.trackName || 'Unknown'}
            </Typography>
            
            {telemetryData.configName && (
              <Typography variant="body2">
                <strong>Config:</strong> {telemetryData.configName}
              </Typography>
            )}
            
            <Typography variant="body2">
              <strong>Car:</strong> {telemetryData.car?.make} {telemetryData.car?.model}
            </Typography>
            
            <Typography variant="body2">
              <strong>Driver:</strong> {telemetryData.user?.nickname || 'Unknown'}
            </Typography>
            
            <Divider sx={{ my: 1 }} />
            
            <Typography variant="body2">
              <strong>Total Data Points:</strong> {telemetryData.locations?.length || 0}
            </Typography>
            
            <Typography variant="body2">
              <strong>Total Laps:</strong> {telemetryData.laps?.length || 0}
            </Typography>
            
            {telemetryData.totalDistance && (
              <Typography variant="body2">
                <strong>Track Distance:</strong> {(telemetryData.totalDistance / 1000).toFixed(2)} km
              </Typography>
            )}
            
            {telemetryData.segments && (
              <Typography variant="body2">
                <strong>Track Segments:</strong> {telemetryData.segments.length}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

        {/* TODO: Add charts in future iteration */}
        {showCharts && (
          <Box sx={{ mt: 2, p: 1, border: '1px dashed grey', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              📊 Telemetry charts will be added in the next iteration
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default TelemetryPanel; 